// DateTimeCalcPage.tsx
import React, { useState, useEffect, useMemo } from 'react'; // Import useEffect and useMemo
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers';
import WorldClock from '../WorldClock';
import DateGapCount from '../DateGapCount';
import AddDays from '../AddDays'; // Import AddDays component
import WorkDaysCalc from '../WorkDaysCalc'; // Import WorkDaysCalc component
import NzPublicHolidays from '../NzPublicHolidays'; // Import NzPublicHolidays component

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

interface DateTimeCalcPageProps {
  initialTabKey?: string; // Optional prop to set the initial tab
}

// Define a type for the keys of tabKeyToIndex
type TabKey = 'add-days' | 'date-gap-count' | 'work-days-calc' | 'world-clock' | 'nz-holidays';

export default function DateTimeCalcPage({ initialTabKey }: DateTimeCalcPageProps) { // Destructure props
  const [value, setValue] = useState(0);
  // Map tab keys to their corresponding index
  const tabKeyToIndex = useMemo(() => ({
    'add-days': 0,
    'date-gap-count': 1,
    'work-days-calc': 2,
    'world-clock': 3,
    'nz-holidays': 4,
  } as Record<TabKey, number>), []); // Assert type here

  useEffect(() => {
    if (initialTabKey && initialTabKey in tabKeyToIndex) {
      setValue(tabKeyToIndex[initialTabKey as TabKey]); // Type assertion for safety
    }
  }, [initialTabKey, tabKeyToIndex]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: '100%' }}> {/* REMOVED MT from here */}        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} aria-label="datetime utility tabs">
            <Tab label="Add Days" {...a11yProps(0)} />
            <Tab label="Date Gap Count" {...a11yProps(1)} />
            <Tab label="Work Days Calculator" {...a11yProps(2)} />
            <Tab label="World Clock" {...a11yProps(3)} />
            <Tab label="NZ Public Holidays" {...a11yProps(4)} />
            {/* Add more tabs as needed */}
          </Tabs>
        </Box>
        <TabPanel value={value} index={0}>
          <AddDays /> {/* Use AddDays component */}
        </TabPanel>
        <TabPanel value={value} index={1}>
          <DateGapCount />
        </TabPanel>
        <TabPanel value={value} index={2}>
          <WorkDaysCalc /> {/* Use WorkDaysCalc component */}
        </TabPanel>
        <TabPanel value={value} index={3}>
          <WorldClock />
        </TabPanel>
        <TabPanel value={value} index={4}>
          <NzPublicHolidays />
        </TabPanel>
      </Box>
    </LocalizationProvider>
  );
}
